/**
 * The RBAC permission CATALOGUE — the vocabulary, not the grants.
 *
 * Foundations 1 (Users rail), B1-T3. principles.md #1 ("one store, one
 * resolver, one owner per noun") and #3 ("nothing that describes a noun is
 * hardcoded — catalogs are seeds, loaded into the store").
 *
 * ─── What this file is ────────────────────────────────────────────────────
 *
 *   - `RbacActionSchema` / `RbacResourceSchema` — the two enums a permission
 *     key is built from. Zod is the type source (CLAUDE.md), so they live
 *     here and every package reads them.
 *   - `permissionKey` — the one key builder: `<action>:<resource>`.
 *   - The catalogue METADATA: which rail-group a resource renders under, how
 *     an action and a resource read to a person, and which actions the picker
 *     warns about. `GET /users/permissions/catalogue` serves it and the Roles
 *     screen renders from it.
 *   - `SYSADMIN_GRANTS` / `sysadminCan()` — the PLATFORM audience's slate,
 *     which this rail does not touch.
 *
 * ─── What it is NOT, any more ─────────────────────────────────────────────
 *
 * It was the role→permission MATRIX: `VIEWER_/MEMBER_/ADMIN_/OWNER_PERMISSIONS`,
 * `ROLE_PERMISSIONS`, `RoleId`, and the three functions that indexed it —
 * `hasPermission(role, action, resource)`, `getPermissionsForRole`,
 * `isRoleId`. Two surfaces answered "what may this person do" from it: the
 * API's `requirePermission` and the browser's `useHasPermission`. A tenant's
 * own roles were in the database and in neither of those answers, so a custom
 * role granted nothing and the Users page and the gateway disagreed.
 *
 * The five bundles are ROWS now — `packages/api/src/db/seeds/roles.seed.ts`,
 * platform data addressed by key, installed by c24/c25 and upserted at boot —
 * and the ONE resolver over them is `packages/api/src/policy/principal.ts` +
 * `can.ts`. The browser holds no bundle at all: it reads `permissions[]` off
 * `GET /users/me` and asks `useHasPermission`, which is a lookup in what the
 * server sent, never a computation.
 *
 *   Server:  requirePermission(action, resource) → resolvePrincipal → can()
 *   Browser: useHasPermission(action, resource)  → the permissions[] from /users/me
 *   Picker:  GET /users/permissions/catalogue    → describePermission() below
 *
 * ─── Adding a new permission ──────────────────────────────────────────────
 * 1. Extend `RbacActionSchema` and/or `RbacResourceSchema` here, and place a
 *    new resource in `PERMISSION_GROUPS` with a label (the module-load
 *    invariant at the bottom fails the boot if you forget).
 * 2. Add the key to `permission-enum.ts` so it is IN the catalogue.
 * 3. Grant it in `roles.seed.ts` to the shipped roles that should hold it,
 *    with a migration if a deployed box must gain it before the next seed run.
 * 4. Wire `app.requirePermission(action, resource)` into the route.
 * 5. Add a `useHasPermission(action, resource)` UI gate in the web app.
 */

import { z } from 'zod';

// ─── Action enum ─────────────────────────────────────────────────────────────

/**
 * What can be done. Keep granular — `delete` is distinct from `write`
 * because delete is reversibility-class-different, and `lifecycle:lock`
 * is distinct from `write` because it triggers downstream notifications
 * + audit emphasis.
 *
 * Action vocabulary (Wave 1a starting set; later waves extend):
 * - read                       passive list / get
 * - write                      create / update mutations (non-destructive)
 * - delete                     destructive — irreversible or recovery-required
 * - invite                     send invitation (email + token)
 * - role:assign                grant or revoke a role on a user
 * - lifecycle:lock             suspend / disable a user
 * - lifecycle:unlock           reinstate a previously locked user
 * - identity-fields:write      regulatory identity (clearance / US-Person /
 *                              HIPAA BAA) — F61 surface; admin-only with
 *                              separation-of-duties (admin can't edit own)
 * - members:manage             add / remove members of a group
 * - mfa:reset                  force re-enroll MFA on another user
 * - export                     materialize data out (CSV / PDF) — recorded
 *                              specially in audit
 * - provision                  platform-level: create new tenant
 * - suspend                    platform-level: suspend a tenant
 */
export const RbacActionSchema = z.enum([
  'read',
  'write',
  'delete',
  'invite',
  'role:assign',
  'lifecycle:lock',
  'lifecycle:unlock',
  'identity-fields:write',
  'members:manage',
  'mfa:reset',
  'export',
  'provision',
  'suspend',
  // Sysadmin-only — Wave 1a / W1a-15. Granted exclusively to the
  // 'sysadmin' role; never appears on any tenant role's grant list.
  'manage',
  // Wave 1b / W1b-08 — tenant-destructive operations gated by typed-confirm + 2-person.
  // Owner-only on the tenant side. These need to be distinct from `delete:tenants`
  // (which is sysadmin-only) because factory reset wipes all tenant data while keeping
  // the tenant row + audit chain.
  'factory:reset',
  'rotate',
  // Wave 2 finish — Library per-doc/per-folder ACL grant management.
  // Distinct from 'write:library' (which gates connector CRUD) because
  // sharing has its own approval flow, audit shape, and UI surface (a
  // ShareDialog rather than a connector form). Granted to admin alongside
  // write/delete; revoking *another* admin's grant requires delete:library.
  'share',
  // Phase 5 PR-0 — Sysadmin runtime control. 'kill' is a destructive-class
  // action on a live process (MCP container, model worker, queue job) that
  // is irreversible WITHOUT data loss (the next probe / cron will respawn)
  // but always disrupts in-flight work. It is gated on sysadmin only, and
  // the UI funnels it through <TypedConfirmDialog>. Kept separate from
  // 'delete' because the resource itself isn't being removed — only the
  // running instance.
  'kill',
  // ─── B2-2 — Memory observability (sysadmin Memory tab) ────────────────
  // 'purge' is a destructive-class action distinct from 'delete'. Wipes
  // BOTH the person's user_memories (ASK-M1) + agent_memories atomically with
  // an explicit operator-supplied reason. Emits MEMORY_BULK_PURGE audit
  // event. Sysadmin-only; tenant roles never receive this grant.
  //
  // Kept separate from 'delete' so a future tenant-admin role with broad
  // delete authority (e.g. delete:settings.data) cannot accidentally
  // wield memory purge — the orthogonality invariant ensures memory
  // purge is locked to the platform scope.
  'purge',
  // ─── AL-C3 — Import/Export bundles ────────────────────────────────────
  // 'apply' is the verb used by IMPORT-EXPORT-SPEC §2.4 for activating
  // a bundle on a tenant (import) AND for materializing a bundle from
  // a tenant (export). The same action token covers both directions
  // because the trust boundary is the same — the cryptographic identity
  // bound to the bundle. IMPORT-EXPORT bundles are signed once and the
  // signature must verify regardless of which side is calling.
  //
  // Kept distinct from 'write' and 'export' because the bundle-class
  // is its own audit surface (the export adds an
  // `argus_live.pack.exported` row distinct from a regular dashboard
  // export), and the gate is composable: e.g. an admin can hold
  // 'export:argus_live' (download a single dashboard as JSON) without
  // holding 'apply:import_export:bundle:argus_pack' (build a signed
  // multi-dashboard bundle). When IMPORT-EXPORT v1.1 adds more bundle
  // kinds (knowledge packs, agent packs, etc.) every kind takes its
  // own `apply:import_export:bundle:<kind>` permission so tenant admins
  // can carve which bundle classes a role can wield.
  'apply',
  // ─── Phase 5b — Fine-tuning SME review ────────────────────────────────
  // 'sme_review' is the verb a tenant power_user (or admin) holds when
  // they have been delegated subject-matter-expert authority on the
  // fine-tuning Gate C reviewer pool. Distinct from 'read' / 'write'
  // because:
  //   - holding 'sme_review:fine_tuning' is the gate for being shown
  //     fine_tuning_sme_assignments rows on the tenant
  //     /fine-tuning/sme-review page
  //   - it does NOT grant read on the broader sysadmin /admin/fine-tuning
  //     surface (sysadmin-only)
  //   - the per-adapter "who is on the SME pool" assignment is itself
  //     gated by 'apply:fine_tuning' (sysadmin) — power_users only earn
  //     the ability to perform the review, not to pick reviewers.
  'sme_review',
  // ─── Foundations 2 / B2d-T2 — support impersonation ───────────────────
  // 'impersonate' is the authority to act AS another person for support.
  // It is its own action, never a shade of `write:users`, for three
  // reasons the rail cares about:
  //   - it is delegable separately: a support role may impersonate without
  //     holding the authority to edit anybody's account, and an editor of
  //     accounts may not thereby act as them;
  //   - decisions.md (2026-09-13 #2) deleted the per-organization
  //     `features.allowImpersonation` switch, so the PERMISSION is the only
  //     gate left and it has to be nameable in the picker;
  //   - every refusal the rule makes (nobody impersonates an Owner, nobody
  //     impersonates themselves) is asked of the SUBJECT, so the caller's
  //     side of the question must be a single key a role can hold.
  // Shipped in the Admin bundle, inherited by Owner (decisions.md
  // 2026-09-08, "Impersonation: in the Admin bundle (Owner inherits)").
  'impersonate',
]);
export type RbacAction = z.infer<typeof RbacActionSchema>;

// ─── Resource enum ───────────────────────────────────────────────────────────

/**
 * What is being acted on. Wave 1a covers the identity / governance / branding
 * surfaces actively touched by Stage 1–5. Later waves extend (knowledge,
 * playgrounds, reports, etc.).
 */
export const RbacResourceSchema = z.enum([
  'users',
  'roles',
  'groups',
  'invitations',
  'audit',
  'governance',
  'tenants',
  'agents',
  // ─── Fix wave 2 RBAC pod #857 — Automate workflows + playbooks ─────────
  // automate-workflows.ts (POST /automate-workflows, PATCH /:id, DELETE /:id,
  // POST /:id/execute) and the playbook routes in the same file (POST
  // /playbooks, DELETE /playbooks/:id, POST /playbooks/:id/instantiate)
  // previously authenticated only via the request context — no RBAC gate.
  // Phase 3 Cycle 1 §3 verified member + viewer could POST + PATCH +
  // DELETE workflows on the live ai1 stack. Mirror the `agents` posture
  // exactly: viewer + member read-only; admin + owner write + delete.
  // Custom-admin roles (W1b-11) can compose write:workflows alone to
  // delegate authoring authority on the automation surface without
  // granting the full admin slate.
  'workflows',
  // ─── Pod-FINAL-CRIT #626 — Studio composable resources ──────────────────
  // Studio tools / skills / prompts mutation routes (workspace-tools.ts,
  // studio-skills.ts, prompt-templates.ts) previously authenticated only
  // with `app.authenticate` — no role gate. Live evidence on ai1 baseline
  // 981eac43 (6 API probes) confirmed member + viewer roles could POST /
  // PUT / PATCH / DELETE Studio tools, skills, and prompt templates. This
  // mirrors the Pod-LLL #571 fix for custom-agents.
  //
  // RBAC matrix (same posture as `agents`):
  //   - viewer + member: read only
  //   - admin:           read + write + delete
  //   - owner:           inherits admin (full slate; no destructive-class
  //                      action distinct from admin because Studio rows
  //                      are config artefacts, not tenant data — same
  //                      logic as `agents` where delete is admin-class)
  //   - sysadmin:        ZERO grants — orthogonality invariant.
  //                      Sysadmin-side system_tools / system_skills /
  //                      system_prompts are managed via admin platform
  //                      routes (admin/catalog-*.ts), not via these
  //                      tenant-scoped resources.
  //
  // Three separate resources rather than one combined `studio` resource:
  // each Studio surface has its own route file, its own audit categories,
  // and a tenant may want to use the W1b-11 custom-admin-role surface to
  // delegate authority on one surface without the others (e.g. a
  // `prompt_author` custom role with write:prompts only).
  'tools',
  'skills',
  'prompts',
  // ─── ADR-0033 §8 D4 — Project Scope (founder-approved 2026-09-04) ──────
  // A project is a LENS over the tenant: `project_sources` decides what
  // every chat, agent and workflow running inside it may retrieve, and the
  // Phase 2 tool bindings decide what it may CALL. Binding and unbinding is
  // therefore a scope-shaping act, not a note-keeping one, and until now it
  // carried NO permission gate at all (routes/v1/projects.ts had only a
  // tenant + project-ownership probe), so any member could widen or narrow
  // a project's knowledge boundary.
  //
  // The key is `write:projects`, not a free-standing `projects.manage_sources`
  // string: `permissionKey()` composes `<action>:<resource>` and splits on the
  // LAST ':', so only the action:resource form is a valid key.
  //
  // RBAC matrix — the `agents` posture exactly:
  //   - viewer + member: read only (the hub still renders for everyone)
  //   - admin:           read + write + delete
  //   - owner:           inherits admin via the spread
  //   - sysadmin:        ZERO grants — the orthogonality invariant.
  //
  // THIS TIGHTENS TODAY'S BEHAVIOUR (D4, deliberate). Member and viewer lose
  // bind/unbind — and, per the P2 review (BF-1 / BF-2), project CREATE
  // (`write:projects`) and project DELETE (`delete:projects`) too, so the
  // whole write surface sits on one key pair instead of leaving the most
  // destructive route open. No data migration is needed because grants are
  // computed from role, never stored. READ is untouched.
  //
  // NO CUSTOM-ROLE ESCAPE HATCH TODAY (P2 review BF-5). An earlier version
  // of this comment said a tenant could compose a custom role holding
  // `write:projects` via the W1b-11 surface. It cannot: `requirePermission`
  // reads the caller's role from the `user_organizations` membership row and
  // indexes ROLE_PERMISSIONS below (plugins/auth.ts), while the W1b-11
  // `custom_admin_roles` / `user_role_assignments` rows are CRUD-only and
  // are read by no auth path. `hasPermission` returns false for anything
  // that is not one of the four RoleIds. Until custom grants are wired into
  // `requirePermission`, the only way to grant a curator these keys is
  // promotion to `admin`. The same false claim is made elsewhere in this
  // file for workflows / argus_live — it is a pre-existing repo-wide doc
  // defect, not one D4 invented, but it is load-bearing here, so it is
  // corrected rather than repeated.
  'projects',
  'mcp',
  'connections',
  'branding',
  'sso',
  // Sysadmin-only — Wave 1a / W1a-15.
  'platform',
  'sysadmins',
  'platform_audit',
  // Wave 1b / W1b-01..W1b-08 — tenant-admin Settings surface.
  // 'settings' is the parent resource; sub-resources distinguish surfaces with
  // different risk profiles + RBAC matrices (data is highest-risk, api keys + webhooks
  // are auth-adjacent, integrations vault external creds, compliance shapes downstream
  // policy, features cascades).
  'settings',
  'settings.features',
  'settings.compliance',
  'settings.api',
  'settings.integrations',
  'settings.data',
  // Wave 1b Stage 2 — Security surface (W1b-09 + W1b-10 + W1b-12 + W1b-13).
  // Sub-resources reflect the multi-tab UI split (Auth / Session / Keys).
  // 'security' is the parent for cross-cutting reads (e.g. Wave 1d
  // unified policy export), but every v1 endpoint pins to the specific
  // sub-resource so RBAC matrices stay precise.
  'security',
  'security.auth',
  'security.session',
  // W1b-12 + W1b-13 — JWT signing keys + KMS + TLS certificates. Same
  // RBAC posture as security.session: admin writes, viewers read,
  // members read. Force-retire / KMS-rotate-complete are 'rotate'-class
  // actions but in v1 they all gate on write:security.keys.
  'security.keys',
  // W1b-14 + W1b-15 + W1b-16 — anomaly rules + incidents + block list.
  // Same posture as security.keys: admin writes, members + viewers read.
  // Bulk-unblock + action upgrade to disable_account are gated by
  // typed-confirm at the UI; the permission is the prerequisite.
  'security.threat',
  // W1b-11 — break-glass accounts + custom admin roles + impersonation
  // audit. Same posture as security.threat: admin writes, members +
  // viewers read. Break-glass unseal + force-end impersonation are
  // typed-confirm-gated at the UI; this permission is the prerequisite.
  'security.privileged',
  // W1b-23 + W1b-24 + W1b-27 — notification rules, channels, delivery log.
  // Same posture as the security.* sub-resources: admin writes (CRUD on
  // rules + channels, retry deliveries, ITAR-approved flip), members +
  // viewers read. Test-fire + test-channel + ITAR-flip are
  // typed-confirm-gated at the UI; this permission is the prerequisite.
  'notifications',
  // Wave 2 / W2-G-07 — RAGAS golden Q&A dataset + run history. Read-only
  // for members/viewers (Quality dashboard surface), admin-write for
  // dataset CRUD + manual run trigger.
  'evaluation',
  // Wave 1b Stage 3 — W1b-17 + W1b-18 — Health & Observability surface.
  // Backs /admin/health overview + per-service detail. Members + viewers
  // read (regulator screenshots the air-gap counter); admins write
  // (probe trigger, schedule maintenance, restart request); owners
  // delete (end maintenance windows). Restart requests are typed-confirm
  // gated at the UI; this permission is the prerequisite.
  'health',
  // Wave 2 finish — Library connectors (OneDrive / SharePoint / GitHub /
  // generic webhook). Admin writes (CRUD, OAuth completion, sync-now,
  // webhook-secret rotation, soft-delete). Members + viewers read.
  // Distinct from W1b-07 'settings.integrations' — different runtime,
  // different security model, different audit shape.
  'library',
  // ─── Foundations 1 / B1-T2 ────────────────────────────────────────────
  // The four in-file role ladders (connections-module, knowledge, wiki,
  // library/connectors) and ~30 raw `role === 'admin'` compares are deleted;
  // the authority they carried needs catalogue keys, and a key must name the
  // surface it gates. `library` gates CONNECTOR config; a tenant member may
  // upload and edit DOCUMENTS without being allowed near a connector, which
  // is exactly the distinction the knowledge.ts ladder drew between its
  // 'member' and 'power_user' rungs.
  'library.documents',
  // The Wiki article surface (routes/v1/wiki.ts). Every mutation there sat
  // behind the WIKI_ROLE_LEVELS 'power_user' rung.
  'wiki',
  // A person's OWN memories (user_memories, ASK-M1 D1) on the Memories page
  // (routes/v1/ask-argus-memories.ts declares read:memory; the owner module
  // scopes every row to the caller). Distinct from the sysadmin-only
  // 'memory_observability' resource, which is the platform-scope view.
  'memory',
  // Agent approval queue administration: snooze, dismiss, escalate, edit an
  // approval's arguments, change approval settings. Deliberately NOT
  // 'write:agents' — agent-approvals-extras.ts:600-607 explains why a role
  // that may author an agent must not thereby be able to pre-stage the
  // arguments an admin later approves.
  'approvals',
  // Insights administration (retention windows, recompute, export config).
  'insights',
  // Wave 3 / M6a — Argus model layer per CATALOG-V1.md + M6-ANCHOR.md.
  // Per I-M6.4 these are additive RBAC strings; no new role UI is built.
  // Tenant admin grants write:argus.* to whoever via the existing W1b-11
  // custom-admin-role surface.
  //
  // 'argus.models'      — tenant-side: read brand catalog (everyone);
  //                       write = flip is_enabled on assignments (admin/owner)
  // 'argus.functions'   — tenant-side: read Trust toggle list (everyone);
  //                       write = toggle Trust functions on/off (admin/owner)
  // 'argus.assignments' — sysadmin-only: per-tenant brand-engine matrix
  //                       (read + write both gated to sysadmin)
  // 'argus.trust'       — alias for argus.functions on the tenant Trust
  //                       page; same RBAC matrix
  'argus.models',
  'argus.functions',
  'argus.assignments',
  'argus.trust',
  // ─── Phase 5 PR-0 — Sysadmin rails ────────────────────────────────────
  // Six rails under /sysadmin/* that gain explicit RBAC scopes here.
  // Each rail's API routes will gate on `app.requireSysadmin` AND check
  // the corresponding privilege; ROLE_PERMISSIONS below grants the full
  // slate to 'sysadmin' (no tenant role gets any of these). Adding the
  // resources here lets PR-A..F call `hasPermission(role, action,
  // resource)` directly when sub-roles are introduced in v2 (e.g.
  // `governance_officer` who only reads governance).
  //
  // The grant matrix may look redundant (sysadmin already holds
  // `manage:*` over the same domain via the existing platform-admin
  // slate) — the redundancy is intentional. v1 sysadmin = "all".
  // v2 will split 'sysadmin' into role variants and the explicit
  // read/write privilege keys per rail are what the variants will
  // pull from.
  //
  // Notes on naming:
  //   - The orthogonality invariant in the v1 RBAC matrix ("sysadmin
  //     has zero tenant-resource permissions") relies on a fixed list
  //     of tenant resources (users / roles / groups / invitations /
  //     audit / governance / agents / mcp / connections / branding /
  //     sso). The Phase 5 sysadmin rails serve PLATFORM-wide views of
  //     conceptually similar surfaces, so they get distinct platform-
  //     scoped resource keys to preserve the invariant. The mapping:
  //         tenant `audit`              → platform `platform_audit` (pre-existing)
  //         tenant `governance`         → platform `platform_governance`
  //         tenant `settings.*`         → platform `platform_settings`
  //   - `platform`, `sysadmins` already exist as sysadmin-only resources
  //     and are reused as-is (no new key).
  //   - `runtime` is wholly new and never appears on a tenant route.
  'runtime',             // PR-D: live runtime view (services, queues, MCP containers)
  'platform_governance', // PR-A: cross-tenant governance config
  'platform_settings',   // PR-E: sysadmin-side platform settings
  // ─── Argus Live — tenant-authored embedded dashboards (PR AL-A-RBAC) ──
  // Per ARGUS-LIVE-SPEC §5.5 + §9.4 (founder directive 2026-05-12).
  //
  // Argus Live is a first-class tenant module that lets users author
  // embedded dashboards (HTML/CSS + MCP tool bindings). Authoring access
  // is gated by the platform's existing permission catalog — no hardcoded
  // role checks. The seven action keys it consumes already exist in
  // RbacActionSchema (`read`, `write`, `delete`, `share`, `export`,
  // `lifecycle:lock`, `lifecycle:unlock`); no new action is introduced.
  //
  // Default grant matrix (per §5.5 of the spec):
  //   - viewer:   read:argus_live only
  //   - member:   read:argus_live only (tenants can grant more via W1b-11 custom roles)
  //   - admin:    full slate (read/write/delete/share/export/lifecycle:lock/unlock)
  //   - owner:    inherits admin (full slate)
  //   - sysadmin: ZERO grants — orthogonality invariant. Sysadmin-side
  //               reference packs are managed via `write:platform_settings`
  //               per spec §AL-C4, not via `argus_live` itself.
  //
  // Two-layer enforcement on edit (§5.5 of the spec): the permission below
  // is the coarse "feature access" gate; per-dashboard ACLs in
  // `tenant_dashboard_grants` provide the fine-grained per-resource gate.
  // Both must pass. This mirrors the Wave 2 Library `write:library` +
  // per-doc grant pattern.
  'argus_live',
  // ─── AL-C3 — Import/Export bundle classes ─────────────────────────────
  // Per IMPORT-EXPORT-SPEC §2.4, every bundle kind that crosses the
  // tenant boundary takes its own resource so admins can carve grants
  // per kind without leaking authority to other kinds. v1 ships one
  // bundle kind (argus_pack — Argus Live dashboards), and the resource
  // name encodes the kind so v1.1's additional kinds (knowledge_pack,
  // agent_pack, etc.) add new resources here without disrupting the
  // existing grant matrix.
  //
  // The full key shape is therefore:
  //   apply:import_export_bundle_argus_pack
  //
  // Underscores (NOT colons) separate the kind segments because the
  // permission parser (`permissionKey()` + the boot-time invariants)
  // uses `lastIndexOf(':')` to split key into action/resource, so a
  // resource string with internal colons would be misread as a multi-
  // segment action. The kind path is preserved by convention.
  //
  // Both directions (import + export) gate on the same key — the trust
  // boundary is the cryptographic identity bound to the bundle, not the
  // direction of data flow. See the comment on the `apply` action above
  // for the rationale.
  'import_export_bundle_argus_pack',
  // ─── B2-2 — Memory observability (sysadmin Memory tab) ─────────────────
  // Sysadmin-only resource for the per-tenant Memory observability dashboard
  // (stats, browse, bulk purge). v1 grants sysadmin read+purge only.
  //
  // Orthogonality invariant: tenant admins do NOT hold ANY grant on
  // `memory_observability`. Tenants who want to inspect their own users'
  // memory use the user-facing /ask-argus/memories page (already shipped
  // in PR #379), NOT this surface. The platform-scope split ensures that
  // accidentally granting a tenant role `read:memory_observability` would
  // be caught by the orthogonality unit test (memory_observability is
  // a platform-scoped resource just like `platform_audit`).
  //
  // Two actions:
  //   read:memory_observability   — stats + browse episodes/profiles + audit
  //   purge:memory_observability  — bulk purge per (tenant,user) with reason
  //
  // `purge` is a destructive-class action distinct from `delete` — it
  // wipes BOTH profile + episode rows for a user atomically, surfaces a
  // dedicated MEMORY_BULK_PURGE audit row, and is gated by a typed-confirm
  // dialog in the UI. Reusing `delete` would risk a future tenant-admin
  // role accidentally being granted purge authority via a generic delete
  // grant; the dedicated action keeps the gate tight.
  'memory_observability',
  // ─── Phase 5a — Fine-Tuning infrastructure (sysadmin-only) ─────────────
  // Per CLAUDE.md §16 Phase 5 + §27 risk #6/#7. The fine-tuning rail at
  // /admin/fine-tuning is sysadmin-only — adapter registry, training data
  // curation, golden set, evaluation runs, and (Phase 5b) the actual
  // trainer + vLLM hot-swap routing decisions all live behind this
  // resource. Tenant roles never receive any 'fine_tuning' grant.
  //
  // Six actions:
  //   read:fine_tuning   — list adapters / data / golden / runs
  //   write:fine_tuning  — create + edit (NOT activate/rollback)
  //   delete:fine_tuning — destructive ops on adapters and golden pairs
  //   apply:fine_tuning  — activate / rollback an adapter (blue/green
  //                        routing decision; distinct from `write`
  //                        because it has runtime impact and is the
  //                        moment of compliance evidence)
  //   approve:fine_tuning — admin-gate approval on an evaluation run
  //   export:fine_tuning  — export training_data for offline trainer use
  //
  // Only the existing `read / write / delete / apply / export` actions
  // are reused; a v1.1 `approve` action would split off `apply` if SOC2
  // separation-of-duties demands it. v1 collapses 'approve' into
  // 'apply' to keep the matrix small.
  'fine_tuning',
]);
export type RbacResource = z.infer<typeof RbacResourceSchema>;

// ─── Permission key shape ────────────────────────────────────────────────────

/**
 * Permission keys are stored as `<action>:<resource>` strings in the role
 * grant matrix. The template-literal type below is exposed for editor
 * autocomplete / call-site type safety, but the matrix itself uses plain
 * strings so future additions don't require a recompile of every consumer.
 */
export type RbacPermission = `${RbacAction}:${RbacResource}`;

/** Canonical key builder. Keeps the action/resource ordering consistent. */
export function permissionKey(
  action: RbacAction,
  resource: RbacResource
): RbacPermission {
  return `${action}:${resource}`;
}

// ─── Catalogue metadata ──────────────────────────────────────────────────────
//
// Foundations 1 (Users rail), B1-T3. What USED to be here was the matrix:
// VIEWER_/MEMBER_/ADMIN_/OWNER_PERMISSIONS, `ROLE_PERMISSIONS`, and the three
// functions that indexed it by an enum value — `hasPermission(role, …)`,
// `getPermissionsForRole`, `isRoleId` — plus the `RoleId` enum itself. The
// bundles are ROWS now (`packages/api/src/db/seeds/roles.seed.ts`, installed by
// c24/c25 and upserted by key), so a tenant role and a shipped role are the
// same kind of thing and the browser cannot hold an answer the store does not.
//
// What stays here is the VOCABULARY and how to render it: the action and
// resource enums above, the key shape, and the label / group / risk metadata
// below. That is not a description of a noun the store owns (principle 3) — it
// is the type source and the presentation of the keys themselves, which is why
// `GET /users/permissions/catalogue` can serve it and the Roles picker can
// render every key from one place instead of a hand-mirrored copy per screen.

/**
 * WHO can ever hold a key on this resource.
 *
 * `'tenant'`   — a tenant principal can hold it, so a tenant role may grant it.
 * `'platform'` — the sysadmin AUDIENCE only. A tenant token is refused by the
 *                audience gate before `can()` is asked, so no tenant role can
 *                confer it and no tenant picker should offer it.
 */
export type PermissionAudience = 'tenant' | 'platform';

/**
 * Which rail a resource belongs to. The Roles permission picker renders one
 * section per group, in the order declared here, so the grid reads the way the
 * product does (docs/system/rails.md).
 *
 * EVERY resource in `RbacResourceSchema` must appear exactly once. The
 * module-load invariant at the bottom of this file asserts it, so adding a
 * resource without placing it fails the API/web boot rather than dropping the
 * key out of the picker silently.
 *
 * AUDIENCE (review B1T3-R2). The group also declares who the keys under it can
 * belong to, because that is the level the split usually falls on: every
 * platform-scoped resource is in the Platform group and nothing else is. The
 * default is carried per GROUP so a new resource cannot be added without
 * answering the question, and it is declared HERE rather than derived from the
 * sysadmin slate because the slate and the audience are not the same set
 * (`sme_review:fine_tuning` is on both the slate and a tenant bundle).
 *
 * Where the group's label is wrong for ONE key, the group says so itself with
 * `keyAudience` (prove PROVE-B1b-C2) — `factory:reset:tenants` is a tenant
 * Owner's action on a platform resource. That is still one declaration read at
 * two grains: `describePermission` resolves the key's override first and the
 * group's label second, and every reader asks `entry.audience`. The rule it
 * replaced was an exception clause in a DIFFERENT file, keyed off a DIFFERENT
 * property (delegability), which is how `delete:tenants` — a key no tenant role
 * can hold — ended up on every tenant surface.
 */
export const PERMISSION_GROUPS = [
  {
    group: 'Users',
    audience: 'tenant',
    resources: ['users', 'roles', 'groups', 'invitations', 'sso'],
  },
  {
    group: 'Library',
    audience: 'tenant',
    resources: ['library', 'library.documents', 'wiki'],
  },
  { group: 'Ask Argus', audience: 'tenant', resources: ['projects', 'memory'] },
  { group: 'Connections', audience: 'tenant', resources: ['connections', 'mcp'] },
  {
    group: 'Studio',
    audience: 'tenant',
    resources: ['tools', 'skills', 'prompts', 'argus.models', 'argus.functions'],
  },
  { group: 'Agents', audience: 'tenant', resources: ['agents', 'argus.assignments'] },
  { group: 'Automate', audience: 'tenant', resources: ['workflows'] },
  { group: 'Approvals', audience: 'tenant', resources: ['approvals'] },
  {
    group: 'Insights & Trust',
    audience: 'tenant',
    resources: ['insights', 'audit', 'governance', 'evaluation', 'argus.trust', 'fine_tuning'],
  },
  { group: 'Argus Live', audience: 'tenant', resources: ['argus_live'] },
  {
    group: 'Admin',
    audience: 'tenant',
    resources: [
      'settings',
      'settings.features',
      'settings.compliance',
      'settings.api',
      'settings.integrations',
      'settings.data',
      'branding',
      'notifications',
      'health',
      'security',
      'security.auth',
      'security.session',
      'security.keys',
      'security.threat',
      'security.privileged',
      'import_export_bundle_argus_pack',
    ],
  },
  {
    group: 'Platform',
    audience: 'platform',
    resources: [
      'tenants',
      'platform',
      'platform_audit',
      'platform_governance',
      'platform_settings',
      'sysadmins',
      'runtime',
      'memory_observability',
    ],
    /**
     * THE ONE KEY ON A PLATFORM RESOURCE THAT A TENANT ROLE HOLDS
     * (prove PROVE-B1b-C2).
     *
     * The `tenants` resource carries both the platform's lifecycle actions
     * (`provision`, `suspend`, `delete`, `manage`, `read`, `write`) and one
     * action the TENANT's own Owner performs on its own tenant:
     * `factory:reset`. The shipped OWNER bundle really holds it
     * (`packages/api/src/db/seeds/roles.seed.ts`, `OWNER`), so it is part of a
     * tenant role's authority and a tenant screen has to be able to describe
     * it. `delete:tenants` is not: no shipped bundle carries it, and it stays
     * `'platform'`.
     *
     * A per-GROUP label cannot say that, and what stood here instead was an
     * exception clause in `TENANT_PERMISSION_CATALOGUE`
     * (`entry.audience === 'tenant' || NON_DELEGABLE_PERMISSIONS.has(...)`) —
     * a second list, deciding catalogue membership on a rule about
     * DELEGATION, that dragged `delete:tenants` onto every tenant surface with
     * it. Declaring the audience where the audience is declared deletes that
     * clause: `describePermission` reads this first and the group's label
     * second, so `entry.audience` is the whole answer for every reader (the
     * catalogue slice, the picker, `findUngrantableGrants`).
     *
     * It does NOT make the key grantable. `nonDelegable` is a separate
     * property of the key (`NON_DELEGABLE_PERMISSIONS` below), the guard in
     * `packages/api/src/policy/grantable.ts` tests it FIRST, and `can()`
     * answers the key by role key and never by grant.
     */
    keyAudience: { 'factory:reset:tenants': 'tenant' },
  },
] as const satisfies ReadonlyArray<{
  group: string;
  audience: PermissionAudience;
  resources: readonly RbacResource[];
  /** Per-KEY audience, where the group's label is wrong for that one key. */
  keyAudience?: Readonly<Record<string, PermissionAudience>>;
}>;

/** resource → group label. Built from `PERMISSION_GROUPS`, never hand-kept. */
const GROUP_OF_RESOURCE: ReadonlyMap<string, string> = new Map(
  PERMISSION_GROUPS.flatMap((entry) =>
    entry.resources.map((resource) => [resource as string, entry.group] as const)
  )
);

/** resource → audience. Built from `PERMISSION_GROUPS`, never hand-kept. */
const AUDIENCE_OF_RESOURCE: ReadonlyMap<string, PermissionAudience> = new Map(
  PERMISSION_GROUPS.flatMap((entry) =>
    entry.resources.map(
      (resource) => [resource as string, entry.audience as PermissionAudience] as const
    )
  )
);

/**
 * key → audience, for the keys whose GROUP label is wrong for them. Built from
 * the `keyAudience` rows of `PERMISSION_GROUPS`, never hand-kept — the audience
 * has ONE declaration site and this is the same one.
 *
 * It narrows nothing and widens nothing on its own: `describePermission` still
 * refuses a key whose resource has no group, so an override cannot smuggle an
 * unplaced key into the tenant vocabulary (principle 4). The invariant at the
 * bottom of this file additionally refuses an override whose resource is not in
 * the group that declared it.
 */
const AUDIENCE_OF_KEY: ReadonlyMap<string, PermissionAudience> = new Map(
  PERMISSION_GROUPS.flatMap((entry) =>
    Object.entries('keyAudience' in entry ? entry.keyAudience : {}).map(
      ([key, audience]) => [key, audience as PermissionAudience] as const
    )
  )
);

/**
 * THE ownership-transfer key, in its three pieces and once.
 *
 * `can(principal, action, resource)` is asked with the two halves and
 * `NON_DELEGABLE_PERMISSIONS` holds the joined key, so before B2e-T2 the same
 * string was spelled in two shapes in two files and nothing could tell if they
 * had drifted — a gate asking `can(p, 'transfer-ownership', 'tenants')` would
 * simply have fallen through to the grant set and answered NO for the Owner
 * while the set still listed the key. Declared here, joined here, and read from
 * here by `policy/ownership.ts` and by the route it gates.
 *
 * It has no catalogue entry on purpose (see `NON_DELEGABLE_PERMISSIONS` below):
 * no role may carry it, so there is nothing for the picker to draw.
 */
export const TRANSFER_OWNERSHIP_ACTION = 'transfer_ownership';
export const TRANSFER_OWNERSHIP_RESOURCE = 'tenants';
export const TRANSFER_OWNERSHIP_PERMISSION =
  `${TRANSFER_OWNERSHIP_ACTION}:${TRANSFER_OWNERSHIP_RESOURCE}` as const;

/**
 * The actions no GRANT can confer — only the shipped Owner ROLE KEY does.
 *
 * Founder, 2026-09-08: "Owner is special only for ownership transfer and tenant
 * deletion." `can()` (packages/api/src/policy/can.ts) answers these three by
 * asking `isPlatformOwner(principal)` and never looks at `principal.grants`, so
 * a tenant that clones Owner into a custom role does NOT thereby hand out the
 * ability to give the tenant away.
 *
 * IT LIVES HERE, NOT IN `can.ts` (review BF-2). The rule was enforced in the
 * gate and unknown to everything that DESCRIBES a key: the catalogue served
 * these as ordinary grantable rows, the picker drew plain checkboxes, and
 * `GET /users/:id/permissions` listed a cloned `factory:reset:tenants` as held
 * while `can()` denied it. One owner per noun — the vocabulary package owns
 * what a key IS, so the marker sits on the catalogue entry and `can.ts` imports
 * the set it enforces.
 *
 * `transfer_ownership:tenants` has no catalogue key: it is DECLARED here, as
 * the three constants above, and both the set below and the route that passes
 * it read those — so the rule exists before the key does and the string is the
 * same on both sides. It used to be a bare literal in this array with a comment
 * promising "the literal the routes pass", written when no route passed it;
 * B2e-T2 shipped `POST /users/:id/transfer-ownership`, and a second spelling of
 * the key would have made that route's gate a no-op that nothing could see.
 *
 * IT IS THE DELEGATION RULE AND NOTHING ELSE (prove PROVE-B1b-C2).
 * `TENANT_PERMISSION_CATALOGUE` used to read this set as a second membership
 * rule — "the tenant audience, PLUS the non-delegable keys" — which put
 * `delete:tenants` on every tenant surface even though no tenant role can ever
 * hold it. Who may be TOLD about a key is `audience`, declared once in
 * `PERMISSION_GROUPS`; whether a GRANT can confer it is this set. Two
 * questions, two properties, and neither stands in for the other.
 */
export const NON_DELEGABLE_PERMISSIONS: ReadonlySet<string> = new Set<string>([
  TRANSFER_OWNERSHIP_PERMISSION,
  'delete:tenants',
  'factory:reset:tenants',
]);

/** Declaration order of the groups — the order the picker renders them in. */
export const PERMISSION_GROUP_ORDER: readonly string[] = PERMISSION_GROUPS.map(
  (entry) => entry.group
);

/** How each action reads to a person. */
const ACTION_LABELS: Record<RbacAction, string> = {
  read: 'Read',
  write: 'Create & edit',
  delete: 'Delete',
  invite: 'Invite',
  'role:assign': 'Assign roles',
  'lifecycle:lock': 'Suspend',
  'lifecycle:unlock': 'Reinstate',
  'identity-fields:write': 'Edit identity fields',
  'members:manage': 'Manage members',
  'mfa:reset': 'Reset MFA',
  export: 'Export',
  provision: 'Provision',
  suspend: 'Suspend',
  manage: 'Manage',
  'factory:reset': 'Factory reset',
  rotate: 'Rotate',
  share: 'Share',
  kill: 'Stop',
  purge: 'Purge',
  apply: 'Apply',
  sme_review: 'SME review',
  impersonate: 'Impersonate',
};

/** How each resource reads to a person. */
const RESOURCE_LABELS: Record<RbacResource, string> = {
  users: 'Users',
  roles: 'Roles',
  groups: 'Groups',
  invitations: 'Invitations',
  audit: 'Audit trail',
  governance: 'Governance',
  tenants: 'Tenants',
  agents: 'Agents',
  workflows: 'Workflows',
  tools: 'Tools',
  skills: 'Skills',
  prompts: 'Prompts',
  projects: 'Projects',
  mcp: 'MCP servers',
  connections: 'Connections',
  branding: 'Branding',
  sso: 'Single sign-on',
  platform: 'Platform',
  sysadmins: 'Platform administrators',
  platform_audit: 'Platform audit',
  settings: 'Settings',
  'settings.features': 'Settings — capabilities',
  'settings.compliance': 'Settings — compliance',
  'settings.api': 'Settings — API keys',
  'settings.integrations': 'Settings — integrations',
  'settings.data': 'Settings — data',
  security: 'Security',
  'security.auth': 'Security — authentication',
  'security.session': 'Security — sessions',
  'security.keys': 'Security — keys',
  'security.threat': 'Security — threats',
  'security.privileged': 'Security — privileged access',
  notifications: 'Notifications',
  evaluation: 'Evaluation',
  health: 'Health',
  library: 'Library sources',
  'library.documents': 'Library documents',
  wiki: 'Wiki',
  memory: 'Memory',
  approvals: 'Approvals',
  insights: 'Insights',
  'argus.models': 'Argus models',
  'argus.functions': 'Argus functions',
  'argus.assignments': 'Argus assignments',
  'argus.trust': 'Argus trust',
  runtime: 'Runtime',
  platform_governance: 'Platform governance',
  platform_settings: 'Platform settings',
  argus_live: 'Argus Live',
  import_export_bundle_argus_pack: 'Argus Pack bundles',
  memory_observability: 'Memory observability',
  fine_tuning: 'Fine-tuning',
};

/**
 * Actions the picker flags with a warning glyph.
 *
 * Presentation, not authority: nothing reads this to decide an allow. It used
 * to be hand-copied into `packages/web/src/routes/users/roles.tsx`; it lives
 * with the rest of the key metadata now so the browser holds no copy.
 */
const HIGH_RISK_ACTIONS: ReadonlySet<string> = new Set<RbacAction>([
  'delete',
  'role:assign',
  'lifecycle:lock',
  'lifecycle:unlock',
  'identity-fields:write',
  'mfa:reset',
  'suspend',
  'provision',
  'factory:reset',
  'purge',
  'kill',
  'rotate',
  // B2d-T2 — acting as another person is the highest-risk read there is:
  // everything the subject can see, the impersonator sees.
  'impersonate',
]);

/**
 * One row of `GET /users/permissions/catalogue`, as a ZOD SCHEMA.
 *
 * Zod is the type source (CLAUDE.md), and this shape crosses a package
 * boundary: the API route types its response with it and the browser infers
 * `PermissionCatalogueRow` from it (review B1T3-R3). Both used to hand-keep the
 * shape — an inline `z.object` in `routes/v1/users.ts` and a TypeScript
 * `interface` in `packages/web/src/lib/use-roles.ts` — so renaming `highRisk`
 * would have left both packages type-checking while the picker's risk column
 * silently emptied.
 */
export const PermissionCatalogueEntrySchema = z.object({
  /** The `<action>:<resource>` wire key. */
  key: z.string(),
  action: RbacActionSchema,
  resource: RbacResourceSchema,
  /** Human label, e.g. `Create & edit · Users`. */
  label: z.string(),
  /** The group the key renders under, from `PERMISSION_GROUPS`. */
  group: z.string(),
  /** Warn before granting. Presentation only — never an authority check. */
  highRisk: z.boolean(),
  /** Who can ever hold this key — its group's audience, or its own override. */
  audience: z.enum(['tenant', 'platform']),
  /** `can()` answers this key by role key, never by grant. */
  nonDelegable: z.boolean(),
});

/** The whole payload of `GET /users/permissions/catalogue`. */
export const PermissionCatalogueResponseSchema = z.object({
  data: z.array(PermissionCatalogueEntrySchema),
});

/**
 * One row of `GET /users/permissions/catalogue` — everything a screen needs to
 * render a permission key without knowing anything else about it.
 *
 * The interface is the NARROWED form of `PermissionCatalogueEntrySchema` above:
 * `key`, `action` and `resource` carry the precise literal unions in code,
 * which the wire schema states as plain strings/enums. The two are pinned to
 * each other by a `satisfies` check in
 * `__tests__/permission-enum.test.ts` — a field added to one and not the other
 * fails there.
 */
export interface PermissionCatalogueEntry {
  /** The `<action>:<resource>` wire key. */
  key: RbacPermission;
  action: RbacAction;
  resource: RbacResource;
  /** Human label, e.g. `Create & edit · Users`. */
  label: string;
  /** The group the key renders under, from `PERMISSION_GROUPS`. */
  group: string;
  /** Warn before granting. Presentation only — never an authority check. */
  highRisk: boolean;
  /**
   * Who can ever hold this key (review B1T3-R2) — its group's audience, or the
   * group's `keyAudience` override for this key (prove PROVE-B1b-C2).
   *
   * `'platform'` means no tenant principal can hold it, so a tenant picker
   * must not offer it as grantable: the escalation guard refuses every one of
   * them, and a checkbox that is always refused is an option the platform
   * cannot honour. It is also what decides whether a TENANT surface may be
   * told the key exists at all (`TENANT_PERMISSION_CATALOGUE`), so this field
   * is the whole answer — no reader adds an exception of its own.
   */
  audience: PermissionAudience;
  /**
   * `true` when `can()` answers this key by ROLE KEY and never by grant —
   * `NON_DELEGABLE_PERMISSIONS` (review BF-2). Ticking it on a custom role
   * confers nothing, so no picker may offer it and no screen may report it as
   * authority a role hands out.
   */
  nonDelegable: boolean;
}

/**
 * Split a `<action>:<resource>` key. The LAST colon separates: actions carry
 * colons (`role:assign`, `lifecycle:lock`, `identity-fields:write`), resources
 * never do. Same rule as `parseRoleGrants` / `toRoleGrants` in `roles.ts`.
 *
 * Returns null unless BOTH halves parse as the declared vocabulary
 * (`RbacActionSchema` × `RbacResourceSchema`) — nothing is inferred, nothing is
 * defaulted (principle 4).
 *
 * IT DOES NOT CHECK `PERMISSION_KEYS` (review BF-7), and cannot: the catalogue
 * is BUILT from `describePermission` below, so a membership test here would be
 * circular. A valid pair the enum does not list — `kill:users` — splits and
 * labels fine. Callers that need "is this a key this release ships?" ask
 * `PERMISSION_KEYS.has(key)` explicitly; callers that must agree with `can()`
 * must NOT, because `can()` is this same vocabulary check and no narrower.
 */
export function splitPermissionKey(
  key: string
): { action: RbacAction; resource: RbacResource } | null {
  const cut = key.lastIndexOf(':');
  if (cut <= 0 || cut === key.length - 1) return null;
  const action = RbacActionSchema.safeParse(key.slice(0, cut));
  const resource = RbacResourceSchema.safeParse(key.slice(cut + 1));
  if (!action.success || !resource.success) return null;
  return { action: action.data, resource: resource.data };
}

/**
 * Describe one permission key. `null` when the key is not a valid
 * `<action>:<resource>` pair whose resource is placed in a group — the caller
 * decides what to do with a key this release cannot place; this function never
 * invents a label for one. As with `splitPermissionKey`, "valid" is the
 * vocabulary, not `PERMISSION_KEYS` (review BF-7).
 */
export function describePermission(key: string): PermissionCatalogueEntry | null {
  const split = splitPermissionKey(key);
  if (!split) return null;
  const canonicalKey = permissionKey(split.action, split.resource);
  const group = GROUP_OF_RESOURCE.get(split.resource);
  // The KEY's own audience wins over its group's, for the keys whose group
  // declares one (`keyAudience`, today `factory:reset:tenants`). Both maps are
  // built from the same `PERMISSION_GROUPS` rows, so this is one declaration
  // read at two grains, not two lists.
  const audience =
    AUDIENCE_OF_KEY.get(canonicalKey) ?? AUDIENCE_OF_RESOURCE.get(split.resource);
  // `group` and the resource audience come from the same `PERMISSION_GROUPS`
  // row, so one missing means the resource is unplaced. Refuse rather than
  // default the audience to 'tenant', which would offer an unplaced key in the
  // tenant picker (principle 4).
  if (!group || !audience) return null;
  return {
    key: canonicalKey,
    action: split.action,
    resource: split.resource,
    label: `${ACTION_LABELS[split.action]} · ${RESOURCE_LABELS[split.resource]}`,
    group,
    highRisk: HIGH_RISK_ACTIONS.has(split.action),
    audience,
    nonDelegable: NON_DELEGABLE_PERMISSIONS.has(canonicalKey),
  };
}

// ─── Sysadmin slate ──────────────────────────────────────────────────────────
//
// The PLATFORM audience, which is out of this rail's scope. Every
// Every platform route keeps its audience gate (requireSysadmin) and its
// per-route check; the check now asks sysadminCan() below instead of indexing
// the deleted tenant matrix.

const SYSADMIN_PERMISSIONS: RbacPermission[] = [
  // Sysadmin operates on the platform routes with audience='argusai-admin'
  // tokens. They hold zero tenant permissions — escalation to a tenant
  // resource requires explicit impersonation (W1a-26) which sets up a
  // tenant token pair for the duration of the impersonation session.
  'read:tenants',
  'write:tenants',
  'provision:tenants',
  'suspend:tenants',
  'delete:tenants',
  // W1a-15 — coarse-grained 'manage' grants for the platform admin
  // namespace. Used by the W1a-15 stub endpoint + the W1a-16+ admin
  // platform routes. Scoped to sysadmin only — no tenant role gets
  // these.
  'manage:platform',
  'manage:tenants',
  'manage:sysadmins',
  'read:platform_audit',
  // Wave 3 / M6a — Argus model + assignment matrix administration.
  // Sysadmin owns the brand catalog, engine pool, wrapper functions,
  // function chains, Trust toggle metadata, and the per-tenant
  // assignment matrix. Manual load/unload is sysadmin-only per I-M6.5.
  'manage:argus.models',
  'manage:argus.functions',
  'read:argus.assignments',
  'write:argus.assignments',
  // ─── Phase 5 PR-0 — Per-rail privilege slate ──────────────────────────
  // Six sysadmin rails (Governance, Platform, Runtime, Audit, Settings,
  // Sysadmins). v1 sysadmin holds ALL of them. v2 may split into
  // sub-roles (e.g. 'governance_officer' = read:platform_governance
  // only). The explicit slate here is the data the PR-A..F routes will
  // gate on via `sysadminCan(isSysadmin, action, resource)`; the existing
  // `manage:*` keys remain valid (we hold both).
  //
  // Note on resource naming: the platform-scoped variants
  // (`platform_governance`, `platform_settings`) preserve the
  // orthogonality invariant in the v1 matrix — sysadmin has ZERO tenant
  // resource permissions; cross-tenant operations always go through a
  // platform-scoped resource. See the comment on the resource-enum
  // additions above for the full mapping.
  //
  // PR-A — Governance (cross-tenant config view)
  'read:platform_governance',
  'write:platform_governance',
  // PR-B — Platform (already covered by manage:platform; we add the
  // explicit read/write pair for symmetry with the other PRs)
  'read:platform',
  'write:platform',
  // PR-C — Audit (sysadmin-side cross-tenant audit; pre-existing
  // 'read:platform_audit' grant is kept above for the W1a-16 endpoints)
  'write:platform_audit',
  'export:platform_audit',
  // PR-D — Runtime (live processes; PR-0 introduces the 'runtime'
  // resource + 'kill' action)
  'read:runtime',
  'write:runtime',
  'kill:runtime',
  // PR-E — Settings (sysadmin platform-wide settings; distinct from
  // tenant 'settings.*' which are tenant-scoped)
  'read:platform_settings',
  'write:platform_settings',
  // PR-F — Sysadmins administration (already covered by manage:sysadmins;
  // adding the explicit read/write pair so PR-F can rely on per-action
  // checks rather than the catch-all 'manage')
  'read:sysadmins',
  'write:sysadmins',
  // ─── B2-2 — Memory observability dashboard ────────────────────────────
  // Sysadmin reads per-tenant memory stats + browse episodes/profiles +
  // audit and can bulk-purge a user's memory with a reason. The route
  // layer requires `requireSysadmin` (audience gate) AND
  // `hasPermission(role, 'read'|'purge', 'memory_observability')`. v2 may
  // split into a dedicated `memory_officer` sub-role; the explicit grants
  // here are what that sub-role will pull from.
  //
  // Orthogonality: NO tenant role (viewer / member / admin / owner) holds
  // any `memory_observability` grant. Tenants who want to inspect their
  // own user memory use the user-facing /ask-argus/memories page from
  // PR #379, NOT this platform-scoped surface.
  'read:memory_observability',
  'purge:memory_observability',
  // ─── Phase 5a — Fine-Tuning infrastructure (sysadmin-only) ─────────────
  // Sysadmin owns the full slate. Tenant roles hold ZERO 'fine_tuning'
  // grants (orthogonality invariant — fine-tuning is platform-managed).
  'read:fine_tuning',
  'write:fine_tuning',
  'delete:fine_tuning',
  'apply:fine_tuning',
  'export:fine_tuning',
  // ─── Phase 5b — SME reviewer authority ────────────────────────────────
  // Sysadmin holds 'sme_review:fine_tuning' for catalog symmetry (a
  // sysadmin can self-assign during testing), but the dedicated tenant
  // grant is added independently to the admin and power_user bundles in
  // packages/api/src/db/seeds/roles.seed.ts.
  'sme_review:fine_tuning',
];

/**
 * The sysadmin slate, frozen for O(1) lookup.
 *
 * Exported because the upgrade path for the PLATFORM audience is not this
 * rail's: the platform routes keep their own audience gate (`requireSysadmin`)
 * and the per-route check below. When the sysadmin audience gets a store of
 * its own, this array moves into it the way the five tenant bundles moved into
 * `roles.seed.ts`.
 */
export const SYSADMIN_GRANTS: ReadonlySet<RbacPermission> = new Set(
  SYSADMIN_PERMISSIONS
);

/**
 * May a PLATFORM caller do `<action>:<resource>`?
 *
 * The replacement for `hasPermission((caller.isSysadmin ? 'sysadmin' :
 * 'viewer') as RoleId, action, resource)`, which was how every
 * platform route asked this question. That call reached into the
 * tenant matrix to answer a platform question, so deleting the matrix would
 * have taken the sysadmin gate with it; asking the sysadmin slate directly is
 * the same answer with no tenant vocabulary in the middle.
 *
 * FAILS CLOSED: a caller who is not a sysadmin holds NOTHING here. The old
 * shape leaned on `'viewer'` holding no platform grant to get the same result;
 * this one does not depend on a tenant role's contents at all.
 *
 * This is NOT the tenant gate. Tenant authority is resolved from the store by
 * `resolvePrincipal` and answered by `can()` in `packages/api/src/policy`;
 * nothing in this file can answer a tenant question any more.
 */
export function sysadminCan(
  isSysadmin: boolean | undefined,
  action: RbacAction,
  resource: RbacResource
): boolean {
  if (isSysadmin !== true) return false;
  return SYSADMIN_GRANTS.has(permissionKey(action, resource));
}

// ─── Module-load invariants ──────────────────────────────────────────────────
//
// Run once at import time so a typo in this file fails the API/web boot
// rather than slipping into production as a silent permission gap.

(function assertInvariants(): void {
  const validActions = new Set<string>(RbacActionSchema.options);
  const validResources = new Set<string>(RbacResourceSchema.options);

  // 1. Every resource is placed in exactly one group. A resource with no
  //    group would be a key the picker cannot render and the catalogue
  //    endpoint would drop — invisible authority, which is the defect this
  //    rail exists to end.
  const placed = new Map<string, string>();
  for (const entry of PERMISSION_GROUPS) {
    for (const resource of entry.resources) {
      if (!validResources.has(resource)) {
        throw new Error(
          `[permissions] PERMISSION_GROUPS places unknown resource '${resource}' in group '${entry.group}'`
        );
      }
      const already = placed.get(resource);
      if (already) {
        throw new Error(
          `[permissions] Resource '${resource}' is in two groups: '${already}' and '${entry.group}'`
        );
      }
      placed.set(resource, entry.group);
    }
  }
  for (const resource of validResources) {
    if (!placed.has(resource)) {
      throw new Error(
        `[permissions] Resource '${resource}' has no group in PERMISSION_GROUPS — it would not appear in the permission catalogue`
      );
    }
  }

  // 1b. Every per-KEY audience override names a real key on a resource THIS
  //     group placed. An override on a resource the group does not own would
  //     be a second audience answer for someone else's key — exactly the
  //     second list this replaced — and an override that does not parse would
  //     never be read at all, so the key would silently keep its group's
  //     audience (principle 4: nothing fails open, nothing is silently
  //     ignored).
  for (const entry of PERMISSION_GROUPS) {
    if (!('keyAudience' in entry)) continue;
    // Widened deliberately: `as const` gives these literal types, and the
    // checks below have to keep working when a second override is added with
    // a different pair of values.
    const groupName: string = entry.group;
    const owned = new Set<string>(entry.resources);
    for (const [key, audience] of Object.entries(entry.keyAudience)) {
      const cut = key.lastIndexOf(':');
      const action = cut > 0 ? key.slice(0, cut) : '';
      const resource = cut > 0 ? key.slice(cut + 1) : '';
      if (!validActions.has(action) || !validResources.has(resource)) {
        throw new Error(
          `[permissions] Group '${groupName}' declares keyAudience for '${key}', which is not an <action>:<resource> pair`
        );
      }
      if (!owned.has(resource)) {
        throw new Error(
          `[permissions] Group '${groupName}' declares keyAudience for '${key}', whose resource '${resource}' it does not place`
        );
      }
      // `AUDIENCE_OF_RESOURCE.get(resource)` IS this group's audience — the
      // map is built from these same rows — read through the map so the check
      // is a value comparison rather than one TypeScript can fold away with
      // the literal types `as const` gives the declaration.
      if (audience === AUDIENCE_OF_RESOURCE.get(resource)) {
        throw new Error(
          `[permissions] Group '${groupName}' declares keyAudience '${audience}' for '${key}', which is already the group's audience — delete the redundant override`
        );
      }
    }
  }

  // 2. Every action and every resource has a label. A missing one would
  //    render as `undefined · Users` on the Roles page.
  for (const action of validActions) {
    if (!ACTION_LABELS[action as RbacAction]) {
      throw new Error(`[permissions] Action '${action}' has no label`);
    }
  }
  for (const resource of validResources) {
    if (!RESOURCE_LABELS[resource as RbacResource]) {
      throw new Error(`[permissions] Resource '${resource}' has no label`);
    }
  }

  // 3. Every sysadmin grant references a valid action x resource pair.
  for (const key of SYSADMIN_GRANTS) {
    const idx = key.lastIndexOf(':');
    if (idx <= 0) {
      throw new Error(`[permissions] Malformed sysadmin permission key: ${key}`);
    }
    const action = key.slice(0, idx);
    const resource = key.slice(idx + 1);
    if (!validActions.has(action)) {
      throw new Error(
        `[permissions] Sysadmin slate grants invalid action '${action}' (in '${key}')`
      );
    }
    if (!validResources.has(resource)) {
      throw new Error(
        `[permissions] Sysadmin slate grants invalid resource '${resource}' (in '${key}')`
      );
    }
  }
})();
